"use client";

import { useEffect } from "react";
import Image from "next/image";
import { useSession } from "@/components/session-provider";

function shade(hex: string, amount: number) {
  const value = parseInt(hex.slice(1), 16);
  const channel = (shift: number) => Math.max(0, Math.min(255, ((value >> shift) & 255) + amount));
  return `#${[16, 8, 0].map((shift) => channel(shift).toString(16).padStart(2, "0")).join("")}`;
}

export function useWorkspaceBrand() {
  const { panel } = useSession();
  if (panel?.mode !== "workspace") return null;
  const { name, logoUrl, accent, hidePoweredBy } = panel.workspace;
  // The API only sends these fields for Company workspaces.
  const branded = accent !== undefined;
  return { name, logoUrl: logoUrl || "", accent: accent || "", hidePoweredBy: !!hidePoweredBy, branded };
}

// Applies the workspace accent as the default. A user's own theme sits on a wrapper element and still wins.
export function BrandTheme() {
  const brand = useWorkspaceBrand();
  const accent = brand?.accent;
  useEffect(() => {
    if (!accent) return;
    const root = document.documentElement;
    root.style.setProperty("--color-accent", accent);
    root.style.setProperty("--color-accent-hover", shade(accent, 24));
    root.style.setProperty("--color-accent-soft", `${accent}1a`);
    return () => {
      root.style.removeProperty("--color-accent");
      root.style.removeProperty("--color-accent-hover");
      root.style.removeProperty("--color-accent-soft");
    };
  }, [accent]);
  return null;
}

export function BrandLogo() {
  const brand = useWorkspaceBrand();
  if (brand?.branded && brand.logoUrl) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={brand.logoUrl} alt="" width={36} height={36} referrerPolicy="no-referrer" className="brand-logo-custom" />;
  }
  return <Image src="/velnqo-logo.png" alt="" width={48} height={48} unoptimized aria-hidden="true" className="shrink-0 scale-[1.75] object-contain" />;
}

export function BrandName() {
  const brand = useWorkspaceBrand();
  if (brand?.branded) return <span>{brand.name}</span>;
  return <span>Velnqo<span className="brand-suffix">Panel</span></span>;
}

export function PoweredBy() {
  const brand = useWorkspaceBrand();
  if (!brand?.branded || brand.hidePoweredBy) return null;
  return <p className="powered-by">Powered by Velnqo</p>;
}
